import React from 'react';
import { MenuItem, Popover, type SxProps } from '@mui/material';
import {
    mdiArrowLeftRight,
    mdiContentCopy,
    mdiFitToPageOutline,
    mdiHandBackLeftOutline,
    mdiRotateLeft,
    mdiRotateRight,
    mdiSelectDrag,
} from '@mdi/js';
import { useSelector } from 'react-redux';
import { GlobalDialogs } from '../../../pdf/GlobalDialogs';
import { contextMenuLocation } from '../../../Redux/ContextMenuSlice';
import type Command from '../../Commands/_Command';
import DocPropsController from '../../Commands/DocPropsController';
import DeleteAnnotController from '../../Commands/DeleteAnnotController';
import ExecuteCommandController from '../../Commands/ExecuteCommandController';
import RotationController from '../../Commands/RotationController';
import ZoomInButton from '../../Commands/ZoomInButton';
import ZoomOutButton from '../../Commands/ZoomOutButton';
import ZoomController from '../../Commands/ZoomController';
import { RotationDirection } from '../../../pdf/Enums';
import contextMenu from '../../../config/contextMenu.json';
import { devError, devLog, generateUniqueKey, upperCaseBegin } from '../../../utils';
import StateCommandButton from '../../Commands/StateCommandButton';
import { devErrorOnce } from '../../../utils/optionalDevelopmentUtils';
import { ReduxUpdater } from '../../../pdf/ReduxUpdater';
import { Box } from '@mui/system';
import type { ILocation } from '../../../pdf/Interfaces';
import { ApryseSDK } from '../../../pdf/ApryseSDK';
import { ApryseUpdater } from '../../../pdf/ApryseUpdater';
import { apryseEventSliceSelectedAnnotationIds } from '../../../Redux/ApryseEventSlice';
import CopyTextController from '../../Commands/CopyTextController';
import { GlobalVariables } from '../../../pdf/GlobalVariables';

export enum ContextButtonSize {
    TextOnly,
    SmallIcon,
    SmallIconWithText,
}

export interface IContextBtnConfig {
    config: Command;
    size: ContextButtonSize;
}

export default () => {
    const popupLocation = useSelector(contextMenuLocation);
    const selectedAnnotationsIds = useSelector(apryseEventSliceSelectedAnnotationIds);

    // Commands that are available for context menu only
    const commandRegistry = React.useMemo(() => {
        return {
            hand: new StateCommandButton(ApryseSDK.ToolsENUM.DEFAULT, mdiHandBackLeftOutline),
            selectionBox: new StateCommandButton(
                ApryseSDK.ToolsENUM.ANNOTATION_EDIT,
                mdiSelectDrag,
            ),
            zoomIn: new ZoomInButton(),
            zoomOut: new ZoomOutButton(),
            zoomOriginalSize: new ZoomController(1.0, mdiHandBackLeftOutline),
            zoomFitWidth: new ZoomController('fitWidth', mdiArrowLeftRight),
            zoomFitPage: new ZoomController('fitPage', mdiFitToPageOutline),
            rotateCCW: new RotationController(RotationDirection.counterclockwise, mdiRotateLeft),
            rotateCW: new RotationController(RotationDirection.clockwise, mdiRotateRight),
            copy: new CopyTextController(),
            copyMeasurementInfo: new ExecuteCommandController(
                'copyMeasurementInfo',
                mdiContentCopy,
                ApryseSDK.copyMeasurementInfo,
            ),
            //copyTextToClipboard: new ExecuteCommandController('copyTextToClipboard', mdiContentCopy, copySelectedTextFromDocument),
            deleteAnnots: new DeleteAnnotController(),
            showDocProps: new DocPropsController(),
        };
    }, [selectedAnnotationsIds]);

    function parseContextMenuOptions(menuKey: string) {
        const contextElements: IContextBtnConfig[] = [];

        contextMenu[menuKey].forEach(g => {
            g.forEach(e => {
                const key = Object.keys(e)[0];
                if (commandRegistry[key] !== undefined) {
                    contextElements.push({
                        config: commandRegistry[key],
                        size: e[key] ?? ContextButtonSize.TextOnly,
                    });
                } else {
                    devErrorOnce(new Error(`ContextCommand with key '${key}' is not defined!`));
                }
            });
        });

        return contextElements;
    }

    const getContextMenuOptions = (popupLocation: ILocation | null): IContextBtnConfig[] => {
        if (popupLocation === null) {
            return [];
        }

        let menuConfig = 'default';
        if (!contextMenu[menuConfig]) {
            throw Error('Missing default context menu configuration!');
        }

        if (ApryseUpdater.selectedAnnotations?.length) {
            menuConfig = 'selectedAnnotation';
            const annot = ApryseUpdater.selectedAnnotations[0];
            const annotationType = `selectedAnnotation${upperCaseBegin(
                ApryseSDK.annotPrettyType(annot),
            )}`;
            if (contextMenu[annotationType] !== undefined) {
                menuConfig = annotationType;
            }
        }
        if (ReduxUpdater.stateName !== ApryseSDK.ToolsENUM.DEFAULT) {
            const stateNameType = `byState${upperCaseBegin(ReduxUpdater.stateName)}`;
            if (contextMenu[stateNameType] !== undefined) {
                menuConfig = stateNameType;
            }
        }

        if (contextMenu[`${ReduxUpdater.viewMode}|${menuConfig}`] !== undefined) {
            menuConfig = `${ReduxUpdater.viewMode}|${menuConfig}`;
        }

        return parseContextMenuOptions(menuConfig);
    };

    const contextConfig = React.useMemo(() => {
        return getContextMenuOptions(popupLocation);
    }, [popupLocation, selectedAnnotationsIds]);

    if (popupLocation === null) {
        return <></>;
    }

    const onContextMenuOptionClick = (command: Command) => {
        devLog('onContextMenuOptionClick', command);
        try {
            command.Callback();
        } catch (e) {
            devError(e as Error);
        } finally {
            GlobalDialogs.closeContextMenu();
        }
    };

    const menuItems = contextConfig.map((x: IContextBtnConfig, i: number) => (
        <MenuItem
            key={generateUniqueKey('ctxMenuBtn', i)}
            onClick={() => onContextMenuOptionClick(x.config)}
            disabled={!x.config.Enabled}
        >
            {x.size !== ContextButtonSize.TextOnly ? (
                <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex' }}>
                    {x.config.GetIcon()}
                </Box>
            ) : null}
            {x.size !== ContextButtonSize.SmallIcon ? x.config.GetTitle() : null}
        </MenuItem>
    ));

    const menuSx: SxProps = {
        '& .MuiPaper-root': {
            boxShadow: '1px 1px 4px 2px rgba(0, 0, 0, 0.25);',
            marginTop: 1,
            color: '#434343',
            minWidth: 180,

            '& .MuiMenu-list': {
                minWidth: '300px',
                padding: 0,
            },

            '& .MuiDivider-root': {
                margin: '0 12px',
            },

            '& .MuiMenuItem-root': {
                fontFamily: '"CeraPro Regular", sans-serif',
                lineHeight: '44px',
                padding: '0 12px',
                fontSize: '15.5px',
            },
        },
    };

    if (GlobalVariables.contextMenuAnchor === null) {
        return <></>;
    }

    const getBoundingClientRect = () => {
        return DOMRect.fromRect({
            x: popupLocation.x,
            y: popupLocation.y,
            width: 1,
            height: 1,
        });
    };

    return (
        <Popover
            open
            anchorEl={{ getBoundingClientRect, nodeType: 1 }}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            onBlur={ev => {
                if (ev.relatedTarget) {
                    (ev.relatedTarget as HTMLElement).click();
                } else {
                    GlobalDialogs.closeContextMenu();
                }
            }}
            sx={menuSx}
        >
            {menuItems}
        </Popover>
    );
};
